import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spinner, formatRelativeTime, cn } from '@dogdisk/shared'
import { AppLayout } from '../components/app-layout'
import { MobileScreen } from '../components/mobile-screen'
import { TabBar } from '../components/tab-bar'
import { useConversations, useMyTickets } from '../_lib/messaging-api'

const AVATAR_COLORS = ['#19caad', '#1971ca', '#FF6B6B', '#F59E0B', '#A855F7', '#EC4899']

export function MessagesPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const { data: dm, isLoading: dmLoading } = useConversations()
  const { data: tickets, isLoading: tLoading } = useMyTickets()

  const conversations = dm?.conversations ?? []
  const filtered = search
    ? conversations.filter((c) => (c.title ?? '').toLowerCase().includes(search.toLowerCase()))
    : conversations

  return (
    <AppLayout>
      <MobileScreen>
        <header className="flex items-center justify-between gap-3 px-6 pt-[44px] pb-3">
          <h1 className="text-[22px] font-bold text-[#1A1A1A]">消息</h1>
          <button type="button" className="text-[13px] text-[#19caad]">全部已读</button>
          <button
            type="button"
            onClick={() => navigate('/messages/new')}
            aria-label="新建"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F6F7F8]"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1A1A1A" strokeWidth="2.4"><path d="M12 5v14M5 12h14" /></svg>
          </button>
        </header>

        <div className="px-6">
          <div className="flex items-center gap-2 rounded-lg bg-[#F6F7F8] px-4 py-3">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索用户或消息"
              className="h-5 flex-1 bg-transparent text-[13px] outline-none placeholder:text-[#9CA3AF]"
            />
          </div>
        </div>

        <main data-testid="p53Mb" className="flex flex-1 flex-col gap-1 px-6 pt-3 pb-safe-tab">
          {/* System / official pinned */}
          <button
            type="button"
            onClick={() => navigate('/profile/notifications')}
            className="mt-2 flex items-center gap-3 rounded-xl bg-[#DBEAFE] px-3 py-3"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#3B82F6]">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" /></svg>
            </span>
            <div className="min-w-0 flex-1 text-left">
              <p className="text-[14px] font-semibold text-[#1A1A1A]">系统通知</p>
              <p className="truncate text-[12px] text-[#9CA3AF]">您的存储空间已使用 90%，请及时清理</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => navigate('/tickets/new')}
            className="flex items-center gap-3 rounded-xl bg-[#FEF3C7] px-3 py-3"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#F59E0B]">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 8a2 2 0 012-2h14a2 2 0 012 2v3a2 2 0 100 2v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 100-2V8z" /></svg>
            </span>
            <div className="min-w-0 flex-1 text-left">
              <p className="text-[14px] font-semibold text-[#1A1A1A]">官方客服</p>
              <p className="truncate text-[12px] text-[#9CA3AF]">
                {tLoading
                  ? '加载中…'
                  : tickets && tickets.tickets.length > 0
                    ? `${tickets.tickets.length} 个工单 · ${tickets.tickets[0]?.title}`
                    : '点击提交新工单'}
              </p>
            </div>
          </button>

          {dmLoading ? (
            <div className="flex justify-center py-8"><Spinner className="text-gray-400" /></div>
          ) : filtered.length > 0 ? (
            <ul className="flex flex-col">
              {filtered.map((c, i) => {
                const color = AVATAR_COLORS[i % AVATAR_COLORS.length]
                const displayName = c.title || c.type || '会话'
                return (
                  <li key={c.conv_id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/messages/${c.conv_id}`)}
                      className="flex w-full items-center gap-3 border-b border-[#F3F4F6] py-3 text-left active:bg-[#F6F7F8]"
                    >
                      <span
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[14px] font-semibold text-white"
                        style={{ background: color }}
                      >
                        {displayName.slice(0, 1).toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="truncate text-[14px] font-semibold text-[#1A1A1A]">{displayName}</p>
                          <span className="shrink-0 text-[11px] text-[#9CA3AF]">
                            {c.last_message_at ? formatRelativeTime(c.last_message_at) : ''}
                          </span>
                        </div>
                        <p className="truncate text-[12px] text-[#9CA3AF]">{c.subtitle}</p>
                      </div>
                      {c.unread_count > 0 && (
                        <span className={cn(
                          'flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-semibold text-white',
                          'bg-[#FF6B6B]',
                        )}>
                          {c.unread_count > 99 ? '99+' : c.unread_count}
                        </span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="py-8 text-center text-[13px] text-[#9CA3AF]">暂无私信</p>
          )}
        </main>

        <TabBar />
      </MobileScreen>
    </AppLayout>
  )
}
